import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import {
    createFieldDate,
    createFieldInput,
    createFieldInputNumber,
    createFieldRadioGroup,
    createFieldTextArea
} from "../../../../components/input/template-builder/template-builder.logic";
import { createColumnDate, createColumnInput, createColumnInputNumber, createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { conditionFormChecker, putField } from "../../../../utils/form";
import { callHazMgtStatus } from "../sell/sell.section";
import numeral from "numeral";

const StorageLocationList = defineAsyncComponent(() => import("./storage-location/StorageLocationList"));

export const callUserObjective = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_OBJECTIVE", value: searchQuery });
export const callUserObjectiveLicenseType = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_OBJECTIVE", value: searchQuery })
        .then(items => items.filter(d => ["2", "5"].includes(d.key)));
export const callHaz = ({ searchQuery, refUserCompanyId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") }
    ];
    if (refUserCompanyId) {
        where.push({ name: "refUserCompany.id", type: "UUID", operator: "=", value: refUserCompanyId });
    }
    if (searchQuery) {
        where.push({ name: "approveCode", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/haz/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "approveCode", "refHazObject.id", "refHazObject.nameTh", "tradeNameTh", "refOperation.id", "refOperation.version", "refOperation.value"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "approveCode", type: "DESC" }]
            }
        })
        .then(page => page.data);
};
export const callHazLicense = ({ searchQuery, refUserCompanyId, refHazSpeciesKey, refHazId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") },
        { name: "refOperation.key", type: "String", operator: "in", value: ["2", "5"] }
    ];
    if (refHazSpeciesKey) {
        where.push({ name: "refSpecies.key", type: "String", operator: "=", value: refHazSpeciesKey });
    }
    if (refHazId) {
        where.push({ name: "hazLicenseDetails.refHaz.id", type: "UUID", operator: "=", value: refHazId });
    }
    if (refUserCompanyId) {
        where.push({ name: "refUserCompany.id", type: "UUID", operator: "=", value: refUserCompanyId });
    }
    if (searchQuery) {
        where.push({ name: "approveCode", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/hazLicense/search", {
            criteria: {
                isDistinct: true,
                select: [
                    "id",
                    "version",
                    "approveCode",
                    "refOperation.id",
                    "refOperation.version",
                    "refOperation.key",
                    "refOperation.value",
                    "refHazLicenseObjective.id",
                    "refHazLicenseObjective.version",
                    "refHazLicenseObjective.key",
                    "refHazLicenseObjective.value",
                    "transportation",
                    "quantity",
                    "quantityReserve",
                    "quantityUse"
                ],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "refOperation.key", type: "ASC" }],
                children: [
                    {
                        name: "hazLicenseCountries",
                        select: ["id", "version", "refCountry.id", "refCountry.version", "refCountry.nameTh"],
                        page: 0,
                        size: 1000000
                    }
                ]
            }
        })
        .then(page => page.data);
};
export const callUserCompany = ({ searchQuery, notId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "active" },
        { name: "isCeo", type: "Boolean", operator: "=", value: true }
    ];
    if (notId) {
        where.push({ name: "id", type: "UUID", operator: "!=", value: notId });
    }
    if (searchQuery) {
        where.push(
            { openBucket: true, name: "companyNameTh", type: "String", operator: "like", value: searchQuery },
            { logic: "OR", closeBucket: true, name: "fullNameTh", type: "String", operator: "like", value: searchQuery }
        );
    }
    return store
        .dispatch("resource/v1/security/user/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "companyNameTh", "companyNameEn", "fullNameTh", "fullNameEn", "refPersonType.*"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "companyNameTh", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callStorageLocation = ({ searchQuery, refHazId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "isLocation", type: "Boolean", operator: "=", value: true }
    ];
    if (refHazId) {
        where.push({ name: "items.refHaz.id", type: "UUID", operator: "=", value: refHazId });
    }
    if (searchQuery) {
        where.push({ name: "name", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/storageLocation/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "name"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "name", type: "ASC" }],
                children: [
                    {
                        name: "items",
                        select: ["*", "refStorageLocationSub.*"],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "refStorageLocationSub.buildingName", type: "ASC" }]
                    }
                ]
            }
        })
        .then(page => page.data);
};
export const callGetHazLicenseReserveAndUse = ({ hazLicenseId }) => {
    return store.dispatch("resource/v1/transaction/stockCard/getHazLicenseReserveAndUse", { hazLicenseId });
};

export const createColumns = () => {
    const columns = [
        createColumnInput({
            title: () => i18n.global.t("views.sellBuy.seller"),
            dataIndex: "refFrom.fullNameTh",
            dataIndexValue: ({ record }) => (record.refFrom?.refPersonType?.key === "1" ? record.refFrom?.companyNameTh : record.refFrom?.fullNameTh),
            customRender: ({ record, column }) => column.dataIndexValue({ record })
        }),
        createColumnInput({
            title: () => i18n.global.t("views.sellBuy.buyer"),
            dataIndex: "refTo.companyNameTh",
            dataIndexValue: ({ record }) => (record.refTo?.refPersonType?.key === "1" ? record.refTo?.companyNameTh : record.refTo?.fullNameTh),
            customRender: ({ record, column }) => column.dataIndexValue({ record })
        }),
        createColumnInput({
            title: () => i18n.global.t("model.hazMgtStorageLocation.refHaz"),
            dataIndex: "storageLocations.0.refHaz.approveCode",
            align: "center",
            search: { name: "storageLocations.refHaz.approveCode" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.hazLicense.refOperation"),
            dataIndex: "storageLocations.0.refStorageLocationSubItem.refHazLicenseTo.refOperation.value",
            inputProp: () => ({ list: callUserObjective, optLabel: "value" }),
            align: "center",
            search: { name: "storageLocations.refStorageLocationSubItem.refHazLicenseTo.refOperation.id" },
        }),
        createColumnInput({
            title: () => i18n.global.t("model.hazMgtStorageLocation.refStorageLocationSubItem"),
            dataIndex: "storageLocations.0.refStorageLocationSubItem.lot",
            align: "center",
            width: 200,
            search: { name: "storageLocations.refStorageLocationSubItem.lot" },
        }),
        createColumnInput({
            title: () => i18n.global.t("model.haz.tradeNameTh"),
            dataIndex: "storageLocations.0.refHaz.tradeNameTh",
            search: { name: "storageLocations.refHaz.tradeNameTh" },
        }),
        createColumnDate({
            title: () => i18n.global.t("views.sellBuy.sellDateTime"),
            dataIndex: "firstStatus.wait.0.statusDatetime",
            dataIndexValue: ({ record }) => moment(record.firstStatus.wait[0].statusDatetime).format("YYYY-MM-DD"),
            customRender: ({ record, column }) => column.dataIndexValue({ record }),
            search: {
                name: "statuses.statusDatetime",
                type: "Datetime",
                operator: "between",
                getValue: ({ value }) => moment(value[0] + "T00:00:00").format("YYYY-MM-DD[T]HH:mm:ssZ"),
                getValue2: ({ value }) => moment(value[1] + "T23:59:59").format("YYYY-MM-DD[T]HH:mm:ssZ")
            }
        }),
        createColumnInput({
            title: () => i18n.global.t("model.hazMgtStorageLocation.refStorageLocation"),
            dataIndex: "storageLocations.0.refStorageLocation.name",
            search: {
                name: "storageLocations.refStorageLocation.name",
            }
        }),
        createColumnInput({
            title: () => i18n.global.t("model.hazMgtStorageLocation.refStorageLocationSub"),
            dataIndex: "storageLocations.0.refStorageLocationSub.buildingName",
            search: {
                name: "storageLocations.refStorageLocationSub.buildingName",
            }
        }),
        createColumnInputNumber({
            title: () => i18n.global.t("model.hazMgtStorageLocation.amount"),
            dataIndex: "storageLocations.0.amount",
            align: "center",
            search: {
                name: "storageLocations.amount",
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.hazMgt.refStatus"),
            dataIndex: "refStatus.attribute.sellText",
            inputProp: () => ({ list: callHazMgtStatus, optLabel: "attribute.sellText" }),
            search: { name: "refStatus.id" },
            align: "center"
        })
    ];
    return columns;
};

export const createField = ({ props, data, key }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forFromRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.sellBuy.descToTo"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forFromRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forFromRemark[0].remark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forToRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.sellBuy.descToFrom"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forToRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forToRemark[0].remark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refHazLicenseTo`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 }, colon: false }),
                label: () => i18n.global.t("views.sellBuy.refHazLicenseType"),
                labelRender: () => " ",
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.refHazLicenseTo,
                            "onUpdate:value": value => {
                                model.storageLocations[0].isSave = true;
                                model.storageLocations[0].refHazLicenseTo = value;

                                lodash.remove(model.storageLocations[0].stores, d => !d.id);
                                model.storageLocations[0].stores.forEach(d => (d.isDelete = true));

                                if (value) {
                                    setIsLoadingApp(true);
                                    callGetHazLicenseReserveAndUse({ hazLicenseId: value.id })
                                        .then(d => {
                                            model.storageLocations[0].refHazLicenseTo.quantityReserve = d.reserve;
                                            model.storageLocations[0].refHazLicenseTo.quantityUse = d.use;
                                        })
                                        .finally(() => setIsLoadingApp(false));
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callHazLicense({
                                    searchQuery,
                                    refUserCompanyId: model.refTo?.id,
                                    refHazSpeciesKey: model.storageLocations?.[0]?.refHaz?.refHazObject?.refSpecies?.key,
                                    refHazId: model.storageLocations?.[0]?.refHaz?.id
                                }),
                            optLabel: "refOperation.value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refFrom`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.sellBuy.seller"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.refFrom?.refPersonType?.key === "1" ? model.refFrom?.companyNameTh : model.refFrom?.fullNameTh,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refHazLicenseTo.approveCode`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.hazMgtStorageLocation.refHazLicenseTo"),
                inputProp: ({ field }) => {
                    const hazLicense = model.storageLocations?.[0]?.refHazLicenseTo;
                    const quantityRemaining = hazLicense
                        ? parseFloat(hazLicense.quantity || 0) - parseFloat(hazLicense.quantityReserve || 0) - parseFloat(hazLicense.quantityUse || 0)
                        : null;
                    return lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.refHazLicenseTo?.approveCode,
                            disabled: true,
                            addonAfter: () =>
                                quantityRemaining ? <span>{i18n.global.t("views.sellBuy.quantityRemaining", { quantityRemaining: numeral(quantityRemaining).format("0,0.00") })}</span> : null
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refHaz.tradeNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.haz.tradeNameTh"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.refHaz?.tradeNameTh,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refHaz.approveCode`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.hazMgtStorageLocation.refHaz"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.refHaz?.approveCode,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.amount`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.hazMgtStorageLocation.amount"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.amount,
                            disabled: true,
                            allowPrecision: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refStorageLocationSubItem.refInformProductQty.refInformProduct.refInform.importExportDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.sellBuy.importDate"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value:
                                model.storageLocations?.[0]?.refStorageLocationSubItem?.refInformProductQty?.refInformProduct?.refInform
                                    ?.importExportDate,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refHazLicenseTo.hazLicenseCountries`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.sellBuy.destinationCountry"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.refHazLicenseTo?.hazLicenseCountries?.map(d => d.refCountry?.nameTh).join(", "),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refHazLicenseTo.refHazLicenseObjective`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.hazLicense.refHazLicenseObjective"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.refHazLicenseTo?.refHazLicenseObjective?.value,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refStorageLocationSubItem.refInformProductQty.refInformProduct.refInform.approveCode`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.sellBuy.refInform"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value:
                                model.storageLocations?.[0]?.refStorageLocationSubItem?.refInformProductQty?.refInformProduct?.refInform?.approveCode,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}storageLocations.0.refHazLicenseTo.transportation`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.hazLicense.transportation"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.storageLocations?.[0]?.refHazLicenseTo?.transportation,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            {
                visible: () => model.storageLocations?.[0].refHazLicenseTo,
                colProp: () => ({ xs: 24, class: "mt-3" }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    model.storageLocations?.[0].refHazLicenseTo; // for refresh table
                    const componentProp = lodash.merge(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}storageLocations.0.stores`,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly,
                            hazMgtStorageLocation: model.storageLocations?.[0]
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "StorageLocationList" } })
                    );
                    return () => <StorageLocationList {...componentProp}></StorageLocationList>;
                }
            }
        ]
    });
};
